import { useCallback, useEffect, useRef, useState } from 'react'
import type { UseFieldArrayReturn, UseFormReturn } from 'react-hook-form'
import { clearEditorDraft, readEditorDraft, writeEditorDraft } from '@/features/consignment-form/draft'
import type { EditorDraft } from '@/features/consignment-form/draft'
import { estimateKey } from '@/features/consignment-form/estimate'
import { emptyItem, itemInputIds } from '@/features/consignment-form/item'
import type { Consignment, Item } from '@/features/consignment-form/schema'

const itemChanged = (values: Item, original: Item) => estimateKey(values) !== estimateKey(original)

export const isItemField = (value: unknown): value is keyof Item =>
  typeof value === 'string' && Object.hasOwn(itemInputIds, value)

/**
 * Which row the editor shows, whether it holds unsaved typing, and the editor draft that
 * survives a reload. `index === null` means a new row is being added.
 */
export function useItemEditor(form: UseFormReturn<Consignment>, items: UseFieldArrayReturn<Consignment, 'items'>) {
  const [index, setIndex] = useState<number | null>(null)
  const [adding, setAdding] = useState(true)
  const [draft, setDraft] = useState<EditorDraft>()
  const dirty = useRef(false)
  const pendingFocus = useRef<keyof Item | null>(null)

  useEffect(() => {
    if (index === null) return
    const field = pendingFocus.current
    if (field) {
      document.getElementById(itemInputIds[field])?.focus()
      pendingFocus.current = null
    }
  }, [index])

  /** Brings back an unfinished row after a reload; `rows` are the saved rows the draft restored. */
  const restore = useCallback((rows: readonly Item[] | undefined) => {
    const editor = readEditorDraft()
    if (editor && (editor.index === null || editor.index < (rows?.length ?? 0))) {
      const original = editor.index === null ? emptyItem : rows?.[editor.index]
      dirty.current = original ? itemChanged(editor.values, original) : false
      setDraft(editor)
      setIndex(editor.index)
      setAdding(editor.index === null)
    } else {
      if (editor) clearEditorDraft()
      if (rows?.length) setAdding(false)
    }
  }, [])

  const close = () => {
    clearEditorDraft()
    dirty.current = false
    setDraft(undefined)
    setIndex(null)
    setAdding(false)
  }

  const open = (at: number) => {
    dirty.current = false
    setDraft(undefined)
    setIndex(at)
  }

  const add = () => {
    dirty.current = false
    setDraft(undefined)
    setAdding(true)
  }

  /** Opens a saved row on the field Submit found at fault. */
  const openAt = (at: number, field: keyof Item) => {
    pendingFocus.current = field
    setIndex(at)
    setAdding(false)
  }

  const save = (values: Item) => {
    if (index === null) items.append(values)
    else items.update(index, values)
    close()
  }

  const remove = () => {
    if (index !== null) items.remove(index)
    close()
  }

  /** Keeps the editor draft in step with what is typed. */
  const change = (values: Item) => {
    const original = index === null ? emptyItem : form.getValues(`items.${index}`)
    dirty.current = itemChanged(values, original)
    writeEditorDraft({ index, values })
  }

  /** Forgets the editor once the consignment is created. */
  const discard = () => {
    clearEditorDraft()
    dirty.current = false
  }

  return {
    index,
    adding,
    draftItem: draft?.values,
    dirty,
    restore,
    open,
    add,
    openAt,
    save,
    remove,
    close,
    change,
    discard,
  }
}
